import React, { useState } from 'react';
import './Products.css';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';

// Import generated preview images
import smilaiImg from '../assets/smilai_preview.webp';
import factoraiImg from '../assets/factorai_preview.webp';
import securaiImg from "../assets/securai_preview.webp";

// Import product tab icons
import securaiIcon from '../assets/Products Icon/SecurAI.webp';
import factoraiIcon from '../assets/Products Icon/FactorAI.webp';
import smilaiIcon from '../assets/Products Icon/SmilAI.webp';

interface ProductStats {
  value: string;
  label: string;
}

interface ProductData {
  name: string;
  tagline: string;
  desc: string;
  features: string[];
  icon: React.ReactNode;
  accentColor: string;
  image: string;
  stats: ProductStats[];
}

interface ProductsProps {
  onRequestDemo: (productName: string) => void;
  preview?: boolean;
}

export const Products: React.FC<ProductsProps> = ({ preview = false }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [blinkIdx, setBlinkIdx] = useState<number | null>(null);

  const productList: ProductData[] = [
    {
      name: 'SecurAI',
      tagline: 'Intelligent Safety, Security & Compliance',
      desc: 'An intelligent platform for workplace safety, security monitoring, compliance management, and real-time incident tracking.',
      features: [
        'Automatic attendance tracking',
        'Real-time PPE compliance checks',
        'Secure workplace access',
        'Instant safety alerts and notifications'
      ],
      icon: <img loading="lazy" src={securaiIcon} alt="Securai" className="tab-product-icon"  title="Vivify Soft Tech" />,
      accentColor: '#2563EB', // Theme primary blue
      image: securaiImg,
      stats: [
        { value: '100ms', label: 'Intrusion trigger response' },
        { value: 'Zero', label: 'Safety violations missed' }
      ]
    },
    {
      name: 'FactorAI',
      tagline: 'Intelligent Manufacturing',
      desc: 'A smart manufacturing platform that improves production visibility, automates processes, monitors equipment, and boosts operational efficiency.',
      features: [
        'Real-time production monitoring',
        'AI-driven operational insights',
        'Improved productivity and efficiency',
        'Smart decision-making with live data'
      ],
      icon: <img loading="lazy" src={factoraiIcon} alt="Factorai" className="tab-product-icon"  title="Vivify Soft Tech" />,
      accentColor: '#2563EB', // Theme primary blue
      image: factoraiImg,
      stats: [
        { value: '+18%', label: 'OEE optimization rate' },
        { value: '-40%', label: 'Predictive downtime' }
      ]
    },
    {
      name: 'SmilAI',
      tagline: 'Intelligent Dentistry',
      desc: 'An AI-powered dental platform that enhances patient care, clinic management, diagnostics, and appointment workflows.',
      features: [
        'AI-assisted dental diagnostics',
        'Smart appointment management',
        'Better patient care and experience',
        'Secure and centralized data management'
      ],
      icon: <img loading="lazy" src={smilaiIcon} alt="Smilai" className="tab-product-icon"  title="Vivify Soft Tech" />,
      accentColor: '#2563EB', // Theme primary blue
      image: smilaiImg,
      stats: [
        { value: '99.4%', label: 'Scan accuracy score' },
        { value: '-25h', label: 'Admin time saved/wk' }
      ]
    }
  ];

  const handleTabChange = (idx: number) => {
    // Trigger blink flash on the clicked tab
    setBlinkIdx(idx);
    setTimeout(() => setBlinkIdx(null), 500);

    if (idx === activeIdx) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIdx(idx);
      setIsTransitioning(false);
    }, 250);
  };

  const activeProduct = productList[activeIdx];

  return (
    <section className="section products-section" id="products">
      <div className="container relative-z">
        {/* Section Header */}
        <div className="section-header centered">
          {/* Eyebrow removed as requested */}
          <h2 className="section-title">AI-Powered Products Built for Real-World Operations</h2>
          <p>Build smarter, faster, and more efficient business operations with AI-powered products.</p>
        </div>

        {preview ? (
          <div className="products-preview-grid-container">
            <div className="products-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
              {productList.map((product, idx) => (
                <div key={idx} className="panel-card panel-hover" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ padding: '0.5rem', borderRadius: '8px', background: `${product.accentColor}15`, color: product.accentColor }}>
                      {product.icon}
                    </div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)' }}>{product.name}</h3>
                  </div>
                  <span style={{ fontSize: '0.8rem', color: product.accentColor, fontWeight: 500 }}>{product.tagline}</span>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{product.desc}</p>
                </div>
              ))}
            </div>
            <div className="centered">
              <Link to="/products">
                <Button variant="primary" size="md" icon={<ArrowRight size={16} />} className="about-hero-primary-btn">
                  Explore Product Showcase
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          /* Tabbed Single Container Widget */
          <div className="products-showcase-container">

            {/* TOP TABS ROW */}
            <div className="products-tabs-row">
              {productList.map((product, idx) => (
                <button
                  key={idx}
                  className={`product-tab-btn ${activeIdx === idx ? 'active' : ''} ${blinkIdx === idx ? 'tab-blink' : ''}`}
                  onClick={() => handleTabChange(idx)}
                  style={{
                    '--tab-accent': product.accentColor,
                    '--tab-blink-color': product.accentColor
                  } as React.CSSProperties}
                >
                  <span className="tab-icon">{product.icon}</span>
                  <span className="tab-name">{product.name}</span>
                </button>
              ))}
            </div>

            {/* MAIN CONTENT DISPLAY AREA */}
            <div className={`product-display-panel ${isTransitioning ? 'fade-out' : 'fade-in'}`}>

              {/* LEFT: Large Image Preview */}
              <div className="product-display-image-box">
                <div
                  className="image-accent-glow"
                  style={{ background: `radial-gradient(circle, ${activeProduct.accentColor}15 0%, transparent 70%)` }}
                />
                <img loading="lazy" src={activeProduct.image}
                  alt={`${activeProduct.name} Showcase`}
                  className="product-showcase-img"
                 title="Vivify Soft Tech" />
              </div>

              {/* RIGHT: Content and Stats */}
              <div className="product-display-content-box">

                {/* Title & Tagline */}
                <h3 className="product-display-title">
                  {activeProduct.name}
                </h3>
                <span className="product-display-tagline" style={{ color: activeProduct.accentColor }}>{activeProduct.tagline}</span>

                {/* Description */}
                <p className="product-display-desc">{activeProduct.desc}</p>

                {/* Core Features Check-List */}
                <div className="product-display-capabilities">
                  <ul className="display-features-list">
                    {activeProduct.features.map((feat, fidx) => (
                      <li key={fidx} className="feature-item">
                        <span className="feat-check" style={{ color: activeProduct.accentColor }}>✓</span>
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>


                {/* CTA Buttons */}
                <div className="product-display-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Link to={`/products/${activeProduct.name.toLowerCase()}`}>
                    <Button variant="secondary" size="md" className="about-hero-secondary-btn">
                      Learn More
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="primary"
                      size="md"
                      className="about-hero-primary-btn"
                      icon={<ArrowRight size={16} />}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      REQUEST DEMO
                    </Button>
                  </Link>
                </div>
              </div>

            </div>

          </div>
        )}
      </div>
    </section>
  );
};

export default Products;
